import { ChevronRightIcon } from "@chakra-ui/icons";
import { AspectRatio, Box, Flex, HStack, Text } from "@chakra-ui/react";
import GCPImage from "components/GCPImage";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import MainFooter from "../../components/Layout/MainFooter";
import PostList from "../../components/PostList";
import api from "../../store/api";
import { BLOG_EN, FILTERED_BLOG_ID } from "constants/blog";
import { useTranslation } from "next-i18next";
import Calltoaction from "components/Calltoaction";

const Youtube = ({ link }) => {
  return (
    <AspectRatio ratio={16 / 9}>
      <iframe width="100%" height="100%" src={link} frameBorder="0"></iframe>
    </AspectRatio>
  );
};

const CarCard = ({ group, router }) => {
  // const router = useRouter();
  let name = "";
  let img = null;
  let imgStrs = group.split("-");
  if (imgStrs[1].includes("Pink")) {
    name = "Model 3 Pink!";
    img = `/images/cars/Tesla-Model3-pink.png`;
  } else if (imgStrs[1].includes("Model3")) {
    name = "Model 3";
    img = `/images/cars/Tesla-Model3-white.png`;
  } else if (imgStrs[1].includes("ModelY")) {
    name = "Model Y";
    img = `/images/cars/Tesla-ModelY-white.png`;
  } else if (imgStrs[1].includes("ModelS")) {
    name = "Model S";
    img = `/images/cars/Tesla-ModelS-black.png`;
  } else {
    name = "Model X";
    img = `/images/cars/Tesla-ModelX-white.png`;
  }
  if (imgStrs[2].includes("LongRange")) {
    if (imgStrs[1].includes("ModelY")) {
      img = img.replace("-white.png", "-LR-black.png");
    } else {
      img = img.replace("-white.png", "-LR-silver.png");
    }
    name = name + " Long Range";
  }
  if (imgStrs[2]?.includes("FSD")) {
    name = name + " FSD";
    if (imgStrs[1].includes("ModelY")) {
      img = img.replace("black", "white");
    } else {
      img = img.replace("silver", "black");
    }
  }
  if (imgStrs[1]?.includes("7座")) {
    img = img.replace("white", "red");
  }
  return (
    <Box
      border="1px solid #CCC"
      rounded="10px"
      mb="10px"
      cursor="pointer"
      _hover={{ border: "2px solid #000" }}
      transition="0.3s ease"
      onClick={() => {
        router.push({
          pathname: "/station",
          query: {
            ...router.query,
            model: imgStrs[1].includes("ModelX") ? "ModelX" : imgStrs[1],
          },
        });
      }}
    >
      <Flex align="center">
        <Box flex="0 0 180px">
          <GCPImage src={img} />
        </Box>
        <Text mx="10px" fontWeight="bold" flex="1 1 auto">
          {name}
        </Text>
      </Flex>
      <HStack
        textAlign="right"
        px="20px"
        h="20px"
        pb="20px"
        fontSize="20px"
        justify="end"
        align="center"
      >
        <Text fontSize="13px!important" lineHeight="1">
          前往選擇顏色
        </Text>
        <ChevronRightIcon />
      </HStack>
    </Box>
  );
};

const PageCTA = ({ text, pathname, ...props }) => {
  const router = useRouter();
  const { t } = useTranslation("common");
  // KEEP: 行銷要求 379 也要有 CTA
  // if (router?.query.post_id_name == 379) return null;
  return (
    <Box
      flex="0 0 100px"
      textAlign="center"
      fontSize="15px"
      fontWeight="bold"
      lineHeight="50px"
      bgColor="#0078E7"
      maxWidth="350px"
      mx="auto"
      mt="30px"
      borderRadius="25px"
      cursor="pointer"
      color="#fff"
      {...props}
    >
      <Link
        href={{
          pathname: pathname || "/station",
          query: router.query,
        }}
      >
        <a style={{ display: "block", flex: "0 0 100px" }}>
          {text || t("orderTeslaNow")}
        </a>
      </Link>
    </Box>
  );
};

const BlogContent = ({ data }) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const contentRef = useRef();
  const [moreList, setMoreList] = useState([]);
  useEffect(() => {
    if (contentRef.current) {
      const content = contentRef.current;
      const carCards = content.querySelectorAll(".car-card");
      if (carCards.length !== 0) {
        carCards.forEach((el) => {
          ReactDOM.render(<CarCard router={router} group={el.id} />, el);
        });
      }
      const youtubes = content.querySelectorAll(".wp-block-embed__wrapper");
      youtubes.forEach((el) => {
        const link = el?.innerText;
        ReactDOM.render(<Youtube link={link} />, el);
      });
    }
  }, [contentRef]);

  useEffect(() => {
    if (!(data?.category === "UDRIVE" || data?.category === null)) {
      window.open(data?.redirect_url, "_self");
    }
  }, [data]);

  useEffect(() => {
    if (!data?.terms) return;
    api.getPosts({ page: 1, term_id: data?.terms[0]?.term_id }).then((resp) => {
      // 最多拿三個
      const list = resp.data.data
        .filter(({ ID }) => BLOG_EN.indexOf(ID) === -1)
        .filter(({ ID }) => FILTERED_BLOG_ID.indexOf(ID) === -1)
        .slice(0, 3);
      setMoreList(list);
    });
  }, [data.terms]);

  return (
    <>
      <Head>
        <title key="title">{data?.post_title}</title>
        <meta
          name="description"
          key="description"
          content={data?.post_excerpt}
        />
        <meta
          key="og_url"
          property="og:url"
          content={`https://www.udrive.city/blog/${data?.ID}-${data?.post_name}`}
        />
        <meta key="og_type" property="og:type" content="article" />
        <meta key="og_title" property="og:title" content={data?.post_title} />
        <meta key="og_image" property="og:image" content={data?.thumbnail} />
        <meta
          key="og_description"
          property="og:description"
          content={data?.post_excerpt}
        />
      </Head>
      <Box maxW="100%" mx="auto" borderTop="1px solid rgba(0, 0, 0, .1)">
        <Box
          w="100%"
          bgImage={`url('${data?.thumbnail}')`}
          bgRepeat="no-repeat"
          bgPosition="center"
          bgSize="cover"
          mb="30px"
        ></Box>
        <Box px="25px" maxW="800px" mx="auto" pb="30px">
          <Flex>
            {data?.terms?.map((term) => {
              return (
                <Text
                  key={term.term_id}
                  fontSize="14px"
                  lineHeight="22px"
                  fontWeight={700}
                  color="#748598"
                  mb="10px"
                  mr
                >
                  {term.name} ·{" "}
                </Text>
              );
            })}
            <Text
              fontSize="14px"
              lineHeight="22px"
              fontWeight={700}
              color="#748598"
              mb="10px"
            >
              {data?.post_date}
            </Text>
          </Flex>
          <Text
            fontSize={{ base: "32px", md: "56px" }}
            lineHeight={{ base: "40px", md: "72px" }}
            fontWeight={700}
            mb="20px"
          >
            {data?.post_title}
          </Text>
          <Box
            borderTop="1px solid rgba(0, 0, 0, .1)"
            w="100%"
            h="1px"
            mx="auto"
          ></Box>
          <Box
            ref={contentRef}
            id="blog-detail"
            pt="20px"
            dangerouslySetInnerHTML={{ __html: data?.post_content }}
          ></Box>
          {!(data.ID == 2888 || data.ID == 2902) && (
            <PageCTA
              text={
                (data.ID == 2341 && "立即填寫問卷") ||
                (data.ID == 272 && "前往合作推廣計畫專區") ||
                ((data.ID == 612 || data.ID == 2211 || data.ID == 3135) &&
                  t("MonthlyPlanContactButton")) ||
                (data.ID == 3475 && t("ContactUDLine")) ||
                ((data.ID == 3799 || data.ID == 3946 || data.ID == 3878) &&
                  "立即加入LINE@洽詢") ||
                (data.ID == 4666 && "立即租車")
              }
              pathname={
                (data.ID == 2341 && "https://forms.gle/CrY8rbZ4Ah9NeUJg7") ||
                (data.ID == 272 && "/partner") ||
                ((data.ID == 612 || data.ID == 2211 || data.ID == 3135) &&
                  "https://lin.ee/BzluV2n") ||
                (data.ID == 3475 && "https://lin.ee/UPkyFwR") ||
                (data.ID == 3799 && "https://lin.ee/1S4m83D") ||
                (data.ID == 3946 && "https://lin.ee/LKzFwy0") ||
                (data.ID == 3878 && "https://lin.ee/l6p1dTh")
              }
              {...((data.ID == 612 ||
                data.ID == 2211 ||
                data.ID == 3135 ||
                data.ID == 3475 ||
                data.ID == 3946 ||
                data.ID == 3878) && { bgColor: "#57be37" })}
            />
          )}
          {/* 379 為「您值得體驗特斯拉」，因為文章內容充斥特斯拉 CTA 所以就不用 */}
          {/* 1339 為亞洲萬里通 */}
          {data.ID != 379 && (
            <Calltoaction
              href={{
                pathname: "/station",
                query: router.query,
              }}
              title={
                data.ID === 1339
                  ? "特斯拉出租｜里程 3 倍送＋抽香港來回機票"
                  : ""
              }
            />
          )}
        </Box>
      </Box>

      {/* 更多文章 */}
      <Box px="25px" maxW="800px" mx="auto" pb="30px">
        <Box
          pt={{ base: "40px", md: "80px" }}
          borderTop="1px solid rgba(0, 0, 0, .1)"
          w="100%"
          h="1px"
          mx="auto"
        ></Box>
        <Text
          color="#333333"
          fontSize={{ base: "20px", md: "40px" }}
          fontWeight={700}
          lineHeight={{ base: "28px", md: "48px" }}
          mb={{ base: "24px", md: "40px" }}
        >
          更多「{data?.terms[0]?.name}」文章
        </Text>
        <PostList data={moreList} />
      </Box>
      <MainFooter />
    </>
  );
};

export async function getServerSideProps(context) {
  const { post_id_name } = context.params;
  const post_id = post_id_name.split("-")[0];
  const resp = await api.getPostDetail(post_id);
  const data = await serverSideTranslations(context.locale, ["common"]);

  return {
    props: { data: resp?.data?.data || {}, ...data },
  };
}

export default BlogContent;
